<template>
  <div>
    <form-section
      title="Importar clientes"
      description="Envie um CSV ou planilha com Documento, Nome Completo, Telefone e Email. Apenas o documento (CPF) é obrigatório."
      icon="mdi-file-upload-outline"
    >
      <v-row dense align="center" class="import-file-row">
        <v-col cols="12" md="8">
          <v-file-input
            v-model="file"
            label="Arquivo CSV, XLS ou XLSX"
            accept=".csv,.xls,.xlsx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            prepend-icon="mdi-paperclip"
            outlined
            dense
            show-size
            truncate-length="28"
            hide-details
            :disabled="loadingPreview || confirming"
            @change="handleFileChange"
          />
        </v-col>
        <v-col cols="12" md="4" class="d-flex align-center">
          <v-switch
            v-model="firstRowIsHeader"
            label="Ignorar a primeira linha"
            color="primary"
            class="mt-0 pt-0 import-header-switch"
            hide-details
            :disabled="loadingPreview || confirming"
            @change="handleHeaderToggle"
          />
        </v-col>
      </v-row>

      <div class="import-actions">
        <v-btn text color="primary" :disabled="loadingPreview || confirming" @click="handleDownloadTemplate">
          <v-icon left>mdi-download</v-icon>
          Baixar modelo
        </v-btn>
        <v-spacer />
        <v-btn text :disabled="loadingPreview || confirming" @click="handleCancel">
          Cancelar
        </v-btn>
        <v-btn
          dark
          color="primary"
          depressed
          :loading="loadingPreview"
          :disabled="!file || confirming"
          @click="handlePreview"
        >
          Analisar arquivo
        </v-btn>
      </div>
    </form-section>

    <form-section
      v-if="preview"
      title="Pré-visualização"
      description="Confira o que será cadastrado. Clientes já existentes serão pulados."
      icon="mdi-table-eye"
      class="mt-4"
    >
      <v-row dense class="mb-4">
        <v-col cols="6" sm="3">
          <div class="import-metric">
            <span class="import-metric__label">Total</span>
            <strong>{{ preview.total }}</strong>
          </div>
        </v-col>
        <v-col cols="6" sm="3">
          <div class="import-metric import-metric--ok">
            <span class="import-metric__label">Novos</span>
            <strong>{{ preview.toCreate }}</strong>
          </div>
        </v-col>
        <v-col cols="6" sm="3">
          <div class="import-metric import-metric--warn">
            <span class="import-metric__label">Já cadastrados</span>
            <strong>{{ preview.existing }}</strong>
          </div>
        </v-col>
        <v-col cols="6" sm="3">
          <div class="import-metric import-metric--danger">
            <span class="import-metric__label">Inválidos</span>
            <strong>{{ preview.invalid }}</strong>
          </div>
        </v-col>
      </v-row>

      <v-tabs v-model="previewTab" background-color="transparent" color="primary" class="mb-3">
        <v-tab>A cadastrar ({{ newItems.length }})</v-tab>
        <v-tab>Já cadastrados ({{ existingItems.length }})</v-tab>
        <v-tab>Inválidos ({{ invalidItems.length }})</v-tab>
      </v-tabs>

      <v-tabs-items v-model="previewTab">
        <v-tab-item>
          <v-data-table
            :headers="headers"
            :items="newItems"
            :items-per-page="10"
            dense
            no-data-text="Nenhum cliente novo para cadastrar"
          >
            <template v-slot:[`item.document`]="{ item }">
              {{ formatDocument(item.document) }}
            </template>
            <template v-slot:[`item.phone`]="{ item }">
              {{ formatPhone(item.phone) }}
            </template>
            <template v-slot:[`item.status`]="{ item }">
              <v-chip small color="green lighten-4" text-color="green darken-3">
                {{ statusLabel(item.status) }}
              </v-chip>
            </template>
          </v-data-table>
        </v-tab-item>
        <v-tab-item>
          <v-data-table
            :headers="headersWithReason"
            :items="existingItems"
            :items-per-page="10"
            dense
            no-data-text="Nenhum cliente já cadastrado no arquivo"
          >
            <template v-slot:[`item.document`]="{ item }">
              {{ formatDocument(item.document) }}
            </template>
            <template v-slot:[`item.phone`]="{ item }">
              {{ formatPhone(item.phone) }}
            </template>
            <template v-slot:[`item.status`]="{ item }">
              <v-chip small color="amber lighten-4" text-color="amber darken-4">
                {{ statusLabel(item.status) }}
              </v-chip>
            </template>
          </v-data-table>
        </v-tab-item>
        <v-tab-item>
          <v-data-table
            :headers="headersWithReason"
            :items="invalidItems"
            :items-per-page="10"
            dense
            no-data-text="Nenhuma linha inválida"
          >
            <template v-slot:[`item.document`]="{ item }">
              {{ formatDocument(item.document) }}
            </template>
            <template v-slot:[`item.phone`]="{ item }">
              {{ formatPhone(item.phone) }}
            </template>
            <template v-slot:[`item.status`]="{ item }">
              <v-chip small color="red lighten-4" text-color="red darken-3">
                {{ statusLabel(item.status) }}
              </v-chip>
            </template>
          </v-data-table>
        </v-tab-item>
      </v-tabs-items>

      <div class="import-actions mt-4">
        <v-btn text :disabled="confirming" @click="handleCancel">Voltar</v-btn>
        <v-spacer />
        <v-btn
          dark
          color="primary"
          depressed
          :loading="confirming"
          :disabled="!preview.toCreate || loadingPreview"
          @click="handleConfirm"
        >
          Confirmar cadastro ({{ preview.toCreate }})
        </v-btn>
      </div>
    </form-section>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import FormSection from "@/components/ui/FormSection.vue";
import { formatCPF, onlyDigits } from "@/helpers/cpf";
import {
  downloadClientsImportTemplate,
  parseClientsImportFile,
  ParsedImportRow,
} from "@/helpers/parseClientsImport";
import {
  ConfirmBulkClients,
  BulkCustomersCreateResponse,
} from "@/services/clients/ConfirmBulkClients";
import {
  PreviewBulkClients,
  BulkCustomersPreviewResponse,
  BulkCustomerPreviewItem,
} from "@/services/clients/PreviewBulkClients";

export default defineComponent({
  name: "ClientBulkImportView",
  components: { FormSection },
  data() {
    return {
      file: null as File | null,
      firstRowIsHeader: true,
      loadingPreview: false,
      confirming: false,
      parsedRows: [] as ParsedImportRow[],
      preview: null as BulkCustomersPreviewResponse | null,
      previewTab: 0,
      headers: [
        { text: "Linha", value: "row", width: 80 },
        { text: "Documento", value: "document" },
        { text: "Nome", value: "name" },
        { text: "Telefone", value: "phone" },
        { text: "Email", value: "email" },
        { text: "Status", value: "status", width: 140 },
      ],
      headersWithReason: [
        { text: "Linha", value: "row", width: 80 },
        { text: "Documento", value: "document" },
        { text: "Nome", value: "name" },
        { text: "Telefone", value: "phone" },
        { text: "Email", value: "email" },
        { text: "Status", value: "status", width: 140 },
        { text: "Motivo", value: "reason" },
      ],
    };
  },
  computed: {
    newItems(): BulkCustomerPreviewItem[] {
      return (this.preview?.items || []).filter((item) => item.status === "new");
    },
    existingItems(): BulkCustomerPreviewItem[] {
      return (this.preview?.items || []).filter((item) => item.status === "exists");
    },
    invalidItems(): BulkCustomerPreviewItem[] {
      return (this.preview?.items || []).filter((item) => item.status === "invalid");
    },
  },
  methods: {
    handleDownloadTemplate() {
      downloadClientsImportTemplate();
    },
    handleCancel() {
      this.$router.push({ name: "clients" });
    },
    handleFileChange() {
      this.preview = null;
      this.parsedRows = [];
    },
    async handleHeaderToggle() {
      this.preview = null;
      if (this.file) {
        await this.handlePreview();
      }
    },
    async handlePreview() {
      if (!this.file) {
        this.$store.dispatch("showSnack", {
          message: "Selecione um arquivo para importar",
          color: "red",
        });
        return;
      }

      this.loadingPreview = true;
      try {
        this.parsedRows = await parseClientsImportFile(
          this.file,
          this.firstRowIsHeader,
        );

        if (!this.parsedRows.length) {
          this.preview = null;
          this.$store.dispatch("showSnack", {
            message: "Nenhuma linha válida encontrada no arquivo",
            color: "red",
          });
          return;
        }

        const previewService = new PreviewBulkClients();
        this.preview = await previewService.execute(
          this.parsedRows.map((row) => ({
            document: row.document,
            name: row.name,
            phone: row.phone,
            email: row.email,
          })),
        );
        this.previewTab = 0;
      } catch (error) {
        this.preview = null;
        this.$store.dispatch("showSnack", {
          message: "Erro ao analisar o arquivo",
          color: "red",
        });
      } finally {
        this.loadingPreview = false;
      }
    },
    async handleConfirm() {
      if (!this.preview || !this.preview.toCreate) {
        return;
      }

      const customers = this.newItems.map((item) => ({
        document: item.document,
        name: item.name,
        phone: item.phone,
        email: item.email,
      }));

      this.confirming = true;
      try {
        const confirmService = new ConfirmBulkClients();
        const result: BulkCustomersCreateResponse = await confirmService.execute(
          customers,
        );

        this.$store.dispatch("showSnack", {
          message: `Importação concluída: ${result.created.length} criados, ${result.skipped.length} pulados, ${result.failed.length} com erro`,
          color: result.failed.length ? "amber darken-2" : "green darken-1",
        });

        this.$router.push({ name: "clients" });
      } catch (error) {
        this.$store.dispatch("showSnack", {
          message: "Erro ao confirmar a importação",
          color: "red",
        });
      } finally {
        this.confirming = false;
      }
    },
    formatDocument(value?: string) {
      return formatCPF(value);
    },
    formatPhone(value?: string) {
      const digits = onlyDigits(value);
      if (digits.length === 11) {
        return digits.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
      }
      if (digits.length === 10) {
        return digits.replace(/(\d{2})(\d{4})(\d{4})/, "($1) $2-$3");
      }
      return value || "";
    },
    statusLabel(status: string) {
      if (status === "new") return "Novo";
      if (status === "exists") return "Já cadastrado";
      return "Inválido";
    },
  },
});
</script>

<style scoped lang="scss">
.import-file-row {
  min-height: 40px;
}

.import-header-switch {
  margin-bottom: 0 !important;
}

.import-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.import-metric {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  padding: 12px 14px;
  background: #fff;

  strong {
    display: block;
    font-size: 1.4rem;
    line-height: 1.2;
  }
}

.import-metric__label {
  display: block;
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.54);
  margin-bottom: 4px;
}

.import-metric--ok strong {
  color: #2e7d32;
}

.import-metric--warn strong {
  color: #f9a825;
}

.import-metric--danger strong {
  color: #c62828;
}
</style>
